import { hasPermission, type UserRole } from '../types/roles';

export interface RoleOption {
  value: UserRole;
  label: string;
  description: string;
}

const ALL_ROLES: readonly UserRole[] = [
  'super_admin',
  'org_admin',
  'campaign_manager',
  'content_creator',
  'analyst',
  'partner_user',
  'agency_operator',
  'external_collaborator',
] as const;

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  campaign_manager: 'Campaign Manager',
  content_creator: 'Content Creator',
  analyst: 'Analyst',
  partner_user: 'Partner User',
  agency_operator: 'Agency Operator',
  external_collaborator: 'External Collaborator',
};

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  super_admin: 'Platform-wide administration across every organization.',
  org_admin: 'Full organization control, including users, billing, settings, and partner management.',
  campaign_manager: 'Creates, manages, approves, and exports campaigns.',
  content_creator: 'Runs workflow steps, uploads documents, and edits generated content.',
  analyst: 'Read-only access to campaigns, dashboards, analytics, and exports.',
  partner_user: 'Campaign-manager capabilities scoped to a single partner partition.',
  agency_operator: 'Campaign-manager capabilities across assigned partner partitions.',
  external_collaborator: 'View-only campaign participation with limited document upload access.',
};

const ROLE_BADGE_CLASSES: Record<UserRole, string> = {
  super_admin: 'bg-purple-100 text-purple-800',
  org_admin: 'bg-red-100 text-red-800',
  campaign_manager: 'bg-blue-100 text-blue-800',
  content_creator: 'bg-emerald-100 text-emerald-800',
  analyst: 'bg-slate-100 text-slate-800',
  partner_user: 'bg-amber-100 text-amber-800',
  agency_operator: 'bg-cyan-100 text-cyan-800',
  external_collaborator: 'bg-zinc-100 text-zinc-800',
};

const ROLE_ACCESS: Record<UserRole, readonly UserRole[]> = {
  super_admin: ALL_ROLES,
  org_admin: [
    'org_admin',
    'campaign_manager',
    'content_creator',
    'analyst',
    'partner_user',
    'agency_operator',
    'external_collaborator',
  ],
  campaign_manager: ['campaign_manager', 'content_creator', 'analyst'],
  content_creator: ['content_creator', 'analyst'],
  analyst: ['analyst'],
  partner_user: ['partner_user', 'campaign_manager', 'content_creator', 'analyst'],
  agency_operator: ['agency_operator', 'campaign_manager', 'content_creator', 'analyst'],
  external_collaborator: ['external_collaborator'],
};

const INVITABLE_ROLES: readonly RoleOption[] = [
  {
    value: 'org_admin',
    label: ROLE_LABELS.org_admin,
    description: ROLE_DESCRIPTIONS.org_admin,
  },
  {
    value: 'campaign_manager',
    label: ROLE_LABELS.campaign_manager,
    description: ROLE_DESCRIPTIONS.campaign_manager,
  },
  {
    value: 'content_creator',
    label: ROLE_LABELS.content_creator,
    description: ROLE_DESCRIPTIONS.content_creator,
  },
  {
    value: 'analyst',
    label: ROLE_LABELS.analyst,
    description: ROLE_DESCRIPTIONS.analyst,
  },
  {
    value: 'partner_user',
    label: ROLE_LABELS.partner_user,
    description: ROLE_DESCRIPTIONS.partner_user,
  },
  {
    value: 'agency_operator',
    label: ROLE_LABELS.agency_operator,
    description: ROLE_DESCRIPTIONS.agency_operator,
  },
  {
    value: 'external_collaborator',
    label: ROLE_LABELS.external_collaborator,
    description: ROLE_DESCRIPTIONS.external_collaborator,
  },
] as const;

export function formatRoleLabel(role: UserRole): string {
  return ROLE_LABELS[role];
}

export function getRoleDescription(role: UserRole): string {
  return ROLE_DESCRIPTIONS[role];
}

export function getRoleBadgeClass(role: UserRole): string {
  return ROLE_BADGE_CLASSES[role];
}

export function roleSatisfies(currentRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_ACCESS[currentRole].includes(requiredRole);
}

export function canManageUsers(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'user.view_list'));
}

export function canInviteUsers(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'user.invite'));
}

export function canChangeUserRoles(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'user.change_role'));
}

export function canRemoveUsers(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'user.remove'));
}

export function canViewBilling(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'org.view_billing'));
}

export function canEditOrganizationSettings(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'org.edit_settings'));
}

export function canDeleteOrganization(role: UserRole | null | undefined): boolean {
  return Boolean(role && hasPermission(role, 'org.delete'));
}

export function canManagePartnerWorkspace(role: UserRole | null | undefined): boolean {
  return Boolean(role && ['super_admin', 'org_admin', 'agency_operator'].includes(role));
}

export function getInvitableRoleOptions(currentRole: UserRole | null | undefined): RoleOption[] {
  if (!currentRole) {
    return [];
  }

  if (currentRole === 'super_admin') {
    return INVITABLE_ROLES.filter((role) => role.value !== 'super_admin');
  }

  if (currentRole === 'org_admin') {
    return INVITABLE_ROLES.filter((role) => role.value !== 'super_admin');
  }

  return [];
}

export function canManageRole(
  currentRole: UserRole | null | undefined,
  targetRole: UserRole | null | undefined,
): boolean {
  if (!currentRole || !targetRole) {
    return false;
  }

  const manageableRoles = getInvitableRoleOptions(currentRole).map((role) => role.value);
  return manageableRoles.includes(targetRole);
}
